import React, { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { loadEntryPoint } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';

import { type ContentProps, Popup, type PopupProps, type TriggerProps } from '@atlaskit/popup';
import Spinner from '@atlaskit/spinner';
import type {
	AnyEntryPoint,
	EntryPointRef,
	GetRuntimePropsFromEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import { usePressablePreloadRef } from '@atlassian/entry-point-use-trigger';
import {
	type ErrorCallback,
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import type { JSResourceReference } from '@atlassian/react-async';
import { useRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useClickToCloseRef } from './useClickToCloseRef';

const emptyEntryPointParams = {} as ParamsOfEntryPoint<AnyEntryPoint>;
const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

type UnionOmit<T, K extends string | number | symbol> = T extends unknown ? Omit<T, K> : never;

type CustomPopupProps = UnionOmit<PopupProps, 'isOpen' | 'content'>;
type EntryPointPropsType<TEntryPoint> = GetRuntimePropsFromEntryPoint<TEntryPoint>;

const FallbackComponent = ({
	fallback,
	onUnmount,
}: {
	fallback?: ReactNode;
	onUnmount: () => void;
}) => {
	useEffect(() => {
		return () => onUnmount();
	}, [onUnmount]);

	return <>{fallback || <Spinner />}</>;
};

export type Props<TEntryPoint> = {
	entryPoint: TEntryPoint;
	entryPointParams?: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<EntryPointPropsType<TEntryPoint>, 'onClose' | 'onContentResized'> & {
		onClose?: () => void;
		onContentResized?: ContentProps['update'];
	};
	errorFallback?: ErrorFallback;
	fallback?: ReactNode;
	forcedReportErrorUFO?: boolean;
	onError?: ErrorCallback;
	preloadOnFocus?: boolean;
	trigger: (triggerProps: TriggerProps) => ReactNode;
} & CustomPopupProps;

export function PopupTrigger<TEntryPoint extends AnyEntryPoint>({
	trigger,
	entryPoint,
	entryPointParams,
	entryPointProps,
	fallback,
	errorFallback,
	onError,
	forcedReportErrorUFO,
	onClose,
	preloadOnFocus = false,
	...popupProps
}: Props<TEntryPoint>): React.JSX.Element {
	const environmentProvider = useRelayEnvironmentProvider();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	const [isOpen, setIsOpen] = useState(false);
	const [content, setContent] = useState<ReactNode | null>(null);

	const updateRef = useRef<(() => void) | null>(null);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
	}, []);

	const handleClose = useCallback(
		(
			event: Event | React.MouseEvent<Element, MouseEvent> | React.KeyboardEvent<Element> | null,
			currentLevel?: any,
		) => {
			setIsOpen(false);
			onClose?.(event, currentLevel);
		},
		[onClose],
	);

	const triggerCloseRef = useClickToCloseRef({
		isOpen,
		handleClose,
	});

	const load = useCallback(
		() =>
			loadEntryPoint<TEntryPoint>(
				environmentProvider,
				entryPoint,
				entryPointParams ?? emptyEntryPointParams,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onLoad = useCallback(
		({ reference: entryPointReference }: { reference: EntryPointRef<TEntryPoint> }) => {
			// avoid re-loading entry-point when closing popup
			if (isOpen) {
				return;
			}

			handleOpen();

			setContent(
				<InternalEntryPointContainer
					id={id}
					entryPointReference={entryPointReference}
					fallback={
						// when the fallback is unmounted we want to trigger an update to the popup position
						<FallbackComponent fallback={fallback} onUnmount={() => updateRef.current?.()} />
					}
					errorFallback={errorFallback}
					runtimeProps={
						{
							...(entryPointProps ?? emptyEntryPointProps),
							onContentResized: () => {
								updateRef.current?.();
							},
							onClose: handleClose,
						} as unknown as EntryPointPropsType<TEntryPoint>
					}
					onError={onError}
					forcedError={forcedReportErrorUFO}
				/>,
			);
		},
		[
			setContent,
			id,
			isOpen,
			entryPointProps,
			fallback,
			forcedReportErrorUFO,
			errorFallback,
			handleOpen,
			onError,
			handleClose,
		],
	);

	const triggerRef = usePressablePreloadRef<EntryPointRef<TEntryPoint>>({
		load,
		onLoad,
		preloadOnFocus,
	});

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => {
			const mergedRef = mergeRefs([triggerRef, triggerProps.ref, triggerCloseRef]);
			return trigger({ ...triggerProps, ref: mergedRef });
		},
		[trigger, triggerRef, triggerCloseRef],
	);

	const popupContent = useCallback(
		({ update }: ContentProps) => {
			updateRef.current = update;
			return content;
		},
		[content],
	);

	return (
		<Popup
			{...popupProps}
			isOpen={isOpen}
			onClose={handleClose}
			trigger={popupTrigger}
			content={popupContent}
		/>
	);
}
